import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatNumberWithCommas } from "@/lib/formatters";
import type { JournalBiblio } from "@/pages/admin/catalog/journal-details.types";

interface JournalDetailsHeaderProps {
  biblio: JournalBiblio;
  volumeCount: number;
  issueCount: number;
  copyCount: number;
}

function countLabel(count: number, singular: string) {
  const plural = singular === "copy" ? "copies" : `${singular}s`;
  return `${formatNumberWithCommas(count)} ${count === 1 ? singular : plural}`;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[140px_1fr] sm:items-start">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

export function JournalDetailsHeader({
  biblio,
  volumeCount,
  issueCount,
  copyCount,
}: JournalDetailsHeaderProps) {
  return (
    <Card>
      <CardContent className="space-y-6 p-6">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Title</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{biblio.title}</h1>
        </div>
        <dl className="space-y-3">
          <DetailRow label="ISSN">
            <span className="font-mono">{biblio.issn}</span>
          </DetailRow>
          {biblio.eIssn ? (
            <DetailRow label="eISSN">
              <span className="font-mono">{biblio.eIssn}</span>
            </DetailRow>
          ) : null}
          {biblio.issnL ? (
            <DetailRow label="ISSN-L">
              <span className="font-mono">{biblio.issnL}</span>
            </DetailRow>
          ) : null}
          <DetailRow label="Editors">
            {biblio.editors?.length ? biblio.editors.join(", ") : "Not recorded"}
          </DetailRow>
          <DetailRow label="Publisher">{biblio.publisher || "Not recorded"}</DetailRow>
          {biblio.websiteUrl ? (
            <DetailRow label="Website Link">
              {/^https?:\/\//i.test(biblio.websiteUrl) ? (
                <a
                  href={biblio.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  {biblio.websiteUrl}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <span>{biblio.websiteUrl}</span>
              )}
            </DetailRow>
          ) : null}
          <DetailRow label="Subjects">
            {biblio.subjects?.length ? (
              <div className="flex flex-wrap gap-2">
                {biblio.subjects.map((subject) => (
                  <Badge key={subject} variant="outline">
                    {subject}
                  </Badge>
                ))}
              </div>
            ) : (
              "No subjects assigned"
            )}
          </DetailRow>
        </dl>
        <div
          className="flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-4 text-sm"
          aria-label="Holdings summary"
        >
          <span>
            <span className="text-muted-foreground">Volumes: </span>
            {countLabel(volumeCount, "volume")}
          </span>
          <span>
            <span className="text-muted-foreground">Issues: </span>
            {countLabel(issueCount, "issue")}
          </span>
          <span>
            <span className="text-muted-foreground">Copies: </span>
            {countLabel(copyCount, "copy")}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
